import { Grid, Stack, Typography } from '@mui/material'
import { DoughnutChart } from 'components/Chart/DoughnutChart'
import { LineChart } from 'components/Chart/LineChart'
import { useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { UIAction } from 'app/slices/UI'
import { useEffect } from 'react'

export default function EventResult() {
  const { id } = useParams<{ id: string }>()
  const event = useAppSelector((s) =>
    s.Event.list.find((f) => f.id === Number(id))
  )
  const ages = event?.joins.map(
    (v) =>
      Math.floor(
        Math.abs(
          new Date(v.createdAt).getTime() - new Date(v.user.birthday).getTime()
        ) /
          (365 * 24 * 60 * 60 * 1000 * 10)
      ) * 10
  )
  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(
      UIAction.setBreadcrumbs([
        {
          text: 'ホーム',
          url: '/',
        },
        {
          text: 'イベント一覧',
          url: '/event',
        },
        {
          text: 'イベント詳細',
          url: '/event/' + id,
        },
        {
          text: 'イベント実績',
          url: '/event/' + id + '/result',
        },
      ])
    )
  }, [dispatch, id])
  return (
    <Grid container spacing={1} mt={2}>
      <Grid item xs={12}>
        <Typography
          variant="h5"
          component="h2"
          sx={{ borderBottom: 2 }}
          fontWeight="bold"
        >
          {event?.name}
        </Typography>
      </Grid>
      {event?.joins.length !== 0 && (
        <>
          <Grid item xs={12} ml={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <Typography variant="h6" component="h3" fontWeight="bold">
                  参加者情報
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} ml={1}>
            <Grid container spacing={4} display="flex" alignItems="stretch">
              <Grid item xs={12} display="flex">
                <Typography
                  variant="body1"
                  component="h6"
                  fontWeight="bold"
                  mr={6}
                >
                  クリア人数 / 参加人数
                </Typography>
                <Typography variant="body1" component="h6">
                  {event?.joins.filter((f) => f.clearFlg).length} /{' '}
                  {event?.joins.length} (人)
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                >
                  <DoughnutChart
                    innerText="男女比"
                    labels={['男性', '女性']}
                    datasets={[
                      {
                        data: [
                          Math.round(
                            (Number(
                              event?.joins.filter((f) => f.user.sex === 'male')
                                .length
                            ) /
                              Number(event?.joins.length)) *
                              100
                          ),
                          Math.round(
                            (Number(
                              event?.joins.filter(
                                (f) => f.user.sex === 'female'
                              ).length
                            ) /
                              Number(event?.joins.length)) *
                              100
                          ),
                        ],
                        backgroundColor: [
                          'RGB(54, 162, 235)',
                          'RGB(255, 99, 132)',
                        ],
                        borderColor: [
                          'RGB(54, 162, 235)',
                          'RGB(255, 99, 132)',
                        ],
                        borderWidth: 2,
                        borderAlign: 'inner',
                      },
                    ]}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                >
                  <DoughnutChart
                    innerText="居住地"
                    labels={['地元', '他県']}
                    datasets={[
                      {
                        data: [
                          Math.round(
                            (Number(
                              event?.joins.filter(
                                (f) =>
                                  f.user.prefectureId === event.prefectureId
                              ).length
                            ) /
                              Number(event?.joins.length)) *
                              100
                          ),
                          Math.round(
                            (Number(
                              event?.joins.filter(
                                (f) =>
                                  f.user.prefectureId !== event.prefectureId
                              ).length
                            ) /
                              Number(event?.joins.length)) *
                              100
                          ),
                        ],
                        backgroundColor: [
                          'RGB(255, 99, 132)',
                          'RGB(54, 162, 235)',
                        ],
                        borderColor: [
                          'RGB(255, 99, 132)',
                          'RGB(54, 162, 235)',
                        ],
                        borderWidth: 2,
                        borderAlign: 'inner',
                      },
                    ]}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                >
                  <DoughnutChart
                    innerText="年齢層"
                    labels={['10代以下', '20代', '30代', '40代', '50代以上']}
                    datasets={[
                      {
                        data: [
                          Math.round(
                            (Number(ages?.filter((age) => age <= 10).length) /
                              Number(event?.joins.length)) *
                              100
                          ),
                          Math.round(
                            (Number(ages?.filter((age) => age === 20).length) /
                              Number(event?.joins.length)) *
                              100
                          ),
                          Math.round(
                            (Number(ages?.filter((age) => age === 30).length) /
                              Number(event?.joins.length)) *
                              100
                          ),
                          Math.round(
                            (Number(ages?.filter((age) => age === 40).length) /
                              Number(event?.joins.length)) *
                              100
                          ),
                          Math.round(
                            (Number(ages?.filter((age) => age >= 50).length) /
                              Number(event?.joins.length)) *
                              100
                          ),
                        ],
                        backgroundColor: [
                          'RGB(255, 99, 132)',
                          'RGB(255, 159, 64)',
                          'RGB(255, 205, 86)',
                          'RGB(75, 192, 192)',
                          'RGB(54, 162, 235)',
                        ],
                        borderColor: [
                          'RGB(255, 99, 132)',
                          'RGB(255, 159, 64)',
                          'RGB(255, 205, 86)',
                          'RGB(75, 192, 192)',
                          'RGB(54, 162, 235)',
                        ],
                        borderWidth: 2,
                        borderAlign: 'inner',
                      },
                    ]}
                  />
                </Stack>
              </Grid>

              {event?.replies.filter((f) => f.surveyId === 1).length ? (
                <Grid item xs={12} md={4}>
                  <Stack
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <DoughnutChart
                      innerText="参加形態"
                      labels={['1名', '2名', '3～5名', '6～10名', '11名以上']}
                      datasets={[
                        {
                          data: [
                            Math.round(
                              (Number(
                                event?.replies.filter(
                                  (f) => f.surveyId === 1 && f.value === '1'
                                ).length
                              ) /
                                Number(
                                  event?.replies.filter((f) => f.surveyId === 1)
                                    .length
                                )) *
                                100
                            ),
                            Math.round(
                              (Number(
                                event?.replies.filter(
                                  (f) => f.surveyId === 1 && f.value === '2'
                                ).length
                              ) /
                                Number(
                                  event?.replies.filter((f) => f.surveyId === 1)
                                    .length
                                )) *
                                100
                            ),
                            Math.round(
                              (Number(
                                event?.replies.filter(
                                  (f) => f.surveyId === 1 && f.value === '3'
                                ).length
                              ) /
                                Number(
                                  event?.replies.filter((f) => f.surveyId === 1)
                                    .length
                                )) *
                                100
                            ),
                            Math.round(
                              (Number(
                                event?.replies.filter(
                                  (f) => f.surveyId === 1 && f.value === '4'
                                ).length
                              ) /
                                Number(
                                  event?.replies.filter((f) => f.surveyId === 1)
                                    .length
                                )) *
                                100
                            ),
                            Math.round(
                              (Number(
                                event?.replies.filter(
                                  (f) => f.surveyId === 1 && f.value === '5'
                                ).length
                              ) /
                                Number(
                                  event?.replies.filter((f) => f.surveyId === 1)
                                    .length
                                )) *
                                100
                            ),
                          ],
                          backgroundColor: [
                            'RGB(255, 99, 132)',
                            'RGB(255, 159, 64)',
                            'RGB(255, 205, 86)',
                            'RGB(75, 192, 192)',
                            'RGB(54, 162, 235)',
                          ],
                          borderColor: [
                            'RGB(255, 99, 132)',
                            'RGB(255, 159, 64)',
                            'RGB(255, 205, 86)',
                            'RGB(75, 192, 192)',
                            'RGB(54, 162, 235)',
                          ],
                          borderWidth: 2,
                          borderAlign: 'inner',
                        },
                      ]}
                    />
                  </Stack>
                </Grid>
              ) : (
                ''
              )}

              {event?.replies.filter((f) => f.surveyId === 2).length ? (
                <Grid item xs={12} md={4}>
                  <Stack
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <DoughnutChart
                      innerText="難易度"
                      labels={[
                        '簡単',
                        'やや簡単',
                        '適当',
                        'やや難しい',
                        '難しい',
                      ]}
                      datasets={[
                        {
                          data: [
                            Math.round(
                              (Number(
                                event?.replies.filter(
                                  (f) => f.surveyId === 2 && f.value === '1'
                                ).length
                              ) /
                                Number(
                                  event?.replies.filter((f) => f.surveyId === 2)
                                    .length
                                )) *
                                100
                            ),
                            Math.round(
                              (Number(
                                event?.replies.filter(
                                  (f) => f.surveyId === 2 && f.value === '2'
                                ).length
                              ) /
                                Number(
                                  event?.replies.filter((f) => f.surveyId === 2)
                                    .length
                                )) *
                                100
                            ),
                            Math.round(
                              (Number(
                                event?.replies.filter(
                                  (f) => f.surveyId === 2 && f.value === '3'
                                ).length
                              ) /
                                Number(
                                  event?.replies.filter((f) => f.surveyId === 2)
                                    .length
                                )) *
                                100
                            ),
                            Math.round(
                              (Number(
                                event?.replies.filter(
                                  (f) => f.surveyId === 2 && f.value === '4'
                                ).length
                              ) /
                                Number(
                                  event?.replies.filter((f) => f.surveyId === 2)
                                    .length
                                )) *
                                100
                            ),
                            Math.round(
                              (Number(
                                event?.replies.filter(
                                  (f) => f.surveyId === 2 && f.value === '5'
                                ).length
                              ) /
                                Number(
                                  event?.replies.filter((f) => f.surveyId === 2)
                                    .length
                                )) *
                                100
                            ),
                          ],
                          backgroundColor: [
                            'RGB(255, 99, 132)',
                            'RGB(255, 159, 64)',
                            'RGB(255, 205, 86)',
                            'RGB(75, 192, 192)',
                            'RGB(54, 162, 235)',
                          ],
                          borderColor: [
                            'RGB(255, 99, 132)',
                            'RGB(255, 159, 64)',
                            'RGB(255, 205, 86)',
                            'RGB(75, 192, 192)',
                            'RGB(54, 162, 235)',
                          ],
                          borderWidth: 2,
                          borderAlign: 'inner',
                        },
                      ]}
                    />
                  </Stack>
                </Grid>
              ) : (
                ''
              )}

              {event?.replies.filter((f) => f.surveyId === 3).length ? (
                <Grid item xs={12} md={4}>
                  <Stack
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <DoughnutChart
                      innerText="時間"
                      labels={['短い', 'やや短い', '適当', 'やや長い', '長い']}
                      datasets={[
                        {
                          data: [
                            Math.round(
                              (Number(
                                event?.replies.filter(
                                  (f) => f.surveyId === 3 && f.value === '1'
                                ).length
                              ) /
                                Number(
                                  event?.replies.filter((f) => f.surveyId === 3)
                                    .length
                                )) *
                                100
                            ),
                            Math.round(
                              (Number(
                                event?.replies.filter(
                                  (f) => f.surveyId === 3 && f.value === '2'
                                ).length
                              ) /
                                Number(
                                  event?.replies.filter((f) => f.surveyId === 3)
                                    .length
                                )) *
                                100
                            ),
                            Math.round(
                              (Number(
                                event?.replies.filter(
                                  (f) => f.surveyId === 3 && f.value === '3'
                                ).length
                              ) /
                                Number(
                                  event?.replies.filter((f) => f.surveyId === 3)
                                    .length
                                )) *
                                100
                            ),
                            Math.round(
                              (Number(
                                event?.replies.filter(
                                  (f) => f.surveyId === 3 && f.value === '4'
                                ).length
                              ) /
                                Number(
                                  event?.replies.filter((f) => f.surveyId === 3)
                                    .length
                                )) *
                                100
                            ),
                            Math.round(
                              (Number(
                                event?.replies.filter(
                                  (f) => f.surveyId === 3 && f.value === '5'
                                ).length
                              ) /
                                Number(
                                  event?.replies.filter((f) => f.surveyId === 3)
                                    .length
                                )) *
                                100
                            ),
                          ],
                          backgroundColor: [
                            'RGB(255, 99, 132)',
                            'RGB(255, 159, 64)',
                            'RGB(255, 205, 86)',
                            'RGB(75, 192, 192)',
                            'RGB(54, 162, 235)',
                          ],
                          borderColor: [
                            'RGB(255, 99, 132)',
                            'RGB(255, 159, 64)',
                            'RGB(255, 205, 86)',
                            'RGB(75, 192, 192)',
                            'RGB(54, 162, 235)',
                          ],
                          borderWidth: 2,
                          borderAlign: 'inner',
                        },
                      ]}
                    />
                  </Stack>
                </Grid>
              ) : (
                ''
              )}
            </Grid>
          </Grid>
        </>
      )}
      {event?.joins.length === 0 && (
        <Grid item xs={12} ml={1} mt={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <Typography variant="h6" component="h3" fontWeight="bold">
                参加者がいません。
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  )
}
